import React from 'react';
import PropTypes from 'prop-types';

import {stableDedupe} from 'common/utils/array-util';

import OptGroup from './opt-group';
import Option from './option';
import SvgSymbol from 'common/components/svg-symbol';
import {t} from 'common/utils/translation-util';
import './index.scss';

/* Filter options property based on search regexp */
function applySearch(options, regexp) {
  // Check if all whitespace or empty
  if (regexp === null) {
    return options;
  }

  return options.filter((datum) =>
    (datum.searchValue || datum.label).match(regexp),
  );
}

export default class OptionList extends React.Component {
  static propTypes = {
    children: PropTypes.any,
    emptySearch: PropTypes.element,
    groups: PropTypes.object,
    hilightSearch: PropTypes.bool,
    multi: PropTypes.bool,
    name: PropTypes.string,
    onChange: PropTypes.func,
    options: PropTypes.arrayOf(PropTypes.object),
    search: PropTypes.instanceOf(RegExp),
    showGroupLabels: PropTypes.bool,
    style: PropTypes.oneOf(['auto', 'checkbox', 'radio', 'right-check']),
    value: PropTypes.any,
    zeroState: PropTypes.element,
  };

  static defaultProps = {
    emptySearch: (
      <p>
        <em>{t('Nothing found')}</em>
      </p>
    ),
    groups: {},
    hilightSearch: true,
    multi: false,
    name: null,
    onChange: () => {},
    options: null,
    search: null,
    showGroupLabels: true,
    style: 'auto',
    value: null,
    zeroState: (
      <p>
        <em>{'Empty'}</em>
      </p>
    ),
  };

  // Given an object of groups (like 'groupName': {label: 'Group Label'})
  // and a list of options (like [{'group': 'groupName', ...} ...],
  // generates a list of unique group names, auto-generating those not
  // specified in the object, but specified in the list of options
  static makeGroupInfos(groups, options) {
    const groupNames = options
      .filter((datum) => datum.group)
      .map((datum) => datum.group);
    const groupNamesUnique = stableDedupe(groupNames);
    return groupNamesUnique.map((name) => ({
      label: name,
      name,
      ...groups[name],
    }));
  }

  /* Build out Option and OptGroup contents from props */
  buildContents() {
    const contents = [];

    let options = this.props.options;

    if (this.props.search) {
      // Filters rows based on column filter property
      options = applySearch(options, this.props.search);
      if (options.length === 0) {
        // Search came back empty, render only empty placeholder
        return this.props.emptySearch;
      }
    }

    // Auto-generate options and opt-groups from data property

    // Generate the groups with the non-loose options
    const optGroupInfos = OptionList.makeGroupInfos(this.props.groups, options);
    const optGroups = optGroupInfos.map((info, index) => {
      // Build child Option components by filtering those that match the group
      const children = options
        .filter((datum) => datum.group === info.name)
        .map((datum, optIndex) => this.buildOption(datum, optIndex));
      return this.buildOptGroup(info, children, index);
    });
    contents.push(...optGroups);

    // Generate loose options (no 'group' specified)
    const looseOptions = options
      .filter((datum) => !datum.group)
      .map((datum, index) => this.buildOption(datum, index));
    contents.push(...looseOptions);

    return contents;
  }

  // Builds a single OptGroup given the OptGroup info, and the children
  // options, and index
  buildOptGroup(optGroupInfo, children, index) {
    return (
      <OptGroup
        key={`optgroup${index}`}
        label={optGroupInfo.label}
        name={optGroupInfo.name}
        showGroupLabels={this.props.showGroupLabels}>
        {children}
      </OptGroup>
    );
  }

  // Build a single Option, given the option info (like {'value': 123,
  // 'label': 'Thing', ...}) and unique index
  buildOption(datum, index) {
    const {multi, hilightSearch, search, value} = this.props;

    const stringValSet = multi
      ? new Set(value.map((val) => String(val)))
      : new Set([String(value)]);

    const isSelected = stringValSet.has(String(datum.value));

    let style = this.props.style;
    if (style === 'auto') {
      style = multi ? 'checkbox' : 'radio';
    }

    return (
      <Option
        hilight={hilightSearch ? search : null}
        isSelected={isSelected}
        key={`option${index}`}
        onSelect={() => this.triggerChange(datum.value)}
        searchValue={datum.searchValue || null}
        style={style}
        value={datum.value}>
        {datum.iconName ? <SvgSymbol name={datum.iconName} /> : null}
        {datum.icon || null}
        <span
          style={{
            paddingLeft: datum.icon ? '30px' : null,
            marginLeft: datum.group ? '15px' : null,
          }}>
          {datum.label}
        </span>
      </Option>
    );
  }

  triggerChange(changedValue) {
    if (!this.props.multi) {
      // Single item, just trigger onChange directly
      this.props.onChange(changedValue);
      return;
    }

    // Otherwise, multi-item, need toggling behavior
    const value = Array.from(this.props.value);
    const valueLocation = value.indexOf(changedValue);
    if (valueLocation !== -1) {
      // Remove it!
      value.splice(valueLocation, 1);
    } else {
      // Add it!
      value.push(changedValue);
    }

    // Freeze the value and trigger an onChange event on it
    Object.freeze(value);
    this.props.onChange(value);
  }

  render() {
    const children = React.Children.toArray(this.props.children);
    const options = this.props.options;
    let contents = [];

    if (this.props.multi && !(this.props.value instanceof Array)) {
      throw new Error('Must have array of values when multi is specified');
    }

    if (children.length < 1 && (!options || options.length < 1)) {
      // Apply zero state if this wraps no children and no options were specified
      contents = this.props.zeroState;
    } else {
      // Build out contents from options and optgroup props
      contents = this.buildContents();
    }

    // If the "name" property is specified, then include a shadowed input
    // Field that contains the value (useful for forms)
    if (this.props.name) {
      const inputValue = this.props.multi
        ? this.props.value.join(',')
        : this.props.value;
      children.push(
        <input
          key={'input'}
          name={this.props.name}
          type={'hidden'}
          value={inputValue}
        />,
      );
    }

    // Return the generated children
    return (
      <div className={'OptionList'}>
        {children}
        {contents}
      </div>
    );
  }
}
